import type { ApprovalRequestId } from "@cmmd-forge/contracts";
import type { ReactElement } from "react";
import { CircleAlertIcon } from "lucide-react";

import { proposedPlanTitle } from "../../proposedPlan";
import type { PendingUserInputDraftAnswer } from "../../pendingUserInput";
import type {
  GitHubActionRequiredRequest,
  PendingApproval,
  PendingUserInput,
} from "../../session-logic";
import type { Thread } from "../../types";
import type { ComposerDisabledReason } from "./composerTurnPlan";
import { ComposerActionRequiredPanel } from "./ComposerActionRequiredPanel";
import { ComposerPendingApprovalPanel } from "./ComposerPendingApprovalPanel";
import { ComposerPendingUserInputPanel } from "./ComposerPendingUserInputPanel";
import { ComposerPlanFollowUpBanner } from "./ComposerPlanFollowUpBanner";

export interface ChatComposerHeaderBannerProps {
  /** A sentence shows; `false` (a held send, no copy) and null show nothing. */
  readonly disabledReason: ComposerDisabledReason;
  readonly activePendingApproval: PendingApproval | null;
  readonly pendingApprovalCount: number;
  readonly activeActionRequired: GitHubActionRequiredRequest | null;
  readonly pendingUserInputs: ReadonlyArray<PendingUserInput>;
  readonly respondingRequestIds: ReadonlyArray<ApprovalRequestId>;
  readonly draftAnswers: Record<string, PendingUserInputDraftAnswer>;
  readonly questionIndex: number;
  readonly error?: string | null;
  readonly showPlanFollowUpPrompt: boolean;
  readonly activeProposedPlan: Thread["proposedPlans"][number] | null;
  readonly onRetryActionRequired: (actionRequired: GitHubActionRequiredRequest) => Promise<void>;
  readonly onToggleOption: (questionId: string, optionLabel: string) => void;
  readonly onAdvance: () => void;
  readonly onFilesChange?:
    | ((
        questionId: string,
        files: import("../../composerDraftStore").ComposerFileAttachment[],
      ) => void)
    | undefined;
}

/**
 * The one banner the Composer shows above its prompt, chosen by urgency.
 *
 * A ladder rather than a stack: two of these at once would push the prompt off
 * the screen, and the lower rungs are all things the person cannot act on until
 * the higher one is cleared. `hasComposerHeader` decides whether any of them
 * applies, and this decides which, from the same facts.
 *
 * Its own module because `ChatComposer` is at its file-size cap and this is a
 * pure render of props with no state of its own, so every later banner change
 * would otherwise have to buy its space back somewhere else in that file.
 */
export function ChatComposerHeaderBanner(
  props: ChatComposerHeaderBannerProps,
): ReactElement | null {
  if (props.disabledReason) {
    return (
      <div className="flex items-start gap-2 px-3 py-2 text-sm text-muted-foreground">
        <CircleAlertIcon
          className="mt-0.5 size-4 shrink-0 text-warning-foreground"
          aria-hidden="true"
        />
        <span>{props.disabledReason}</span>
      </div>
    );
  }
  if (props.activePendingApproval) {
    return (
      <ComposerPendingApprovalPanel
        approval={props.activePendingApproval}
        pendingCount={props.pendingApprovalCount}
      />
    );
  }
  if (props.activeActionRequired) {
    return (
      <ComposerActionRequiredPanel
        actionRequired={props.activeActionRequired}
        onRetry={props.onRetryActionRequired}
      />
    );
  }
  if (props.pendingUserInputs.length > 0) {
    return (
      <ComposerPendingUserInputPanel
        pendingUserInputs={[...props.pendingUserInputs]}
        respondingRequestIds={[...props.respondingRequestIds]}
        answers={props.draftAnswers}
        questionIndex={props.questionIndex}
        error={props.error ?? null}
        onToggleOption={props.onToggleOption}
        onAdvance={props.onAdvance}
        onFilesChange={props.onFilesChange ?? (() => {})}
      />
    );
  }
  if (props.showPlanFollowUpPrompt && props.activeProposedPlan) {
    return (
      <ComposerPlanFollowUpBanner
        key={props.activeProposedPlan.id}
        planTitle={proposedPlanTitle(props.activeProposedPlan.planMarkdown) ?? null}
      />
    );
  }
  return null;
}
